<!-- Backdrop -->
<div
  *ngIf="showAddAddressSection"
  class="fixed inset-0 bg-black bg-opacity-50 z-[90]"
  (click)="onBackdropClick($event)"
></div>

<!-- Modal Container -->
<section
  class="overflow-hidden bg-white fixed inset-0 h-full shadow-lg z-[100] w-full max-w-full max-md:bottom-0 max-md:top-auto max-md:h-auto max-md:max-h-[90vh] max-md:rounded-t-3xl max-md:flex max-md:flex-col sm:max-w-[600px] sm:fixed sm:right-0 sm:top-0 sm:inset-auto sm:h-full sm:rounded-none"
  [class.invisible]="!showAddAddressSection"
  [class.pointer-events-none]="!showAddAddressSection"
  [ngClass]="
    showAddAddressSection
      ? 'translate-x-0 max-md:translate-y-0'
      : 'translate-x-full max-md:translate-y-full'
  "
>
  <!-- Header -->
  <header
    class="flex-shrink-0 flex justify-between items-center px-4 py-4 w-full text-xl font-bold leading-none bg-white border-b border-solid border-b-[color:var(--Input-field-Stroke,#D4D4D4)] text-blue-950 max-md:px-6 max-md:py-6 max-md:text-2xl max-md:sticky max-md:top-0 max-md:z-10"
  >
    <!-- Mobile drag handle indicator -->
    <div
      class="max-md:flex max-md:justify-center max-md:w-full max-md:absolute max-md:top-2 max-md:left-1/2 max-md:transform max-md:-translate-x-1/2 hidden"
    >
      <div class="w-12 h-1 bg-gray-300 rounded-full"></div>
    </div>

    <h1 class="truncate max-md:text-xl max-md:mt-4">
      <span *ngIf="type === 'address'"
        >{{ isEditMode ? "Edit" : "Add" }} Address
      </span>
      <span *ngIf="type === 'billing'"
        >{{ isEditMode ? "Edit" : "Add" }} Billing Address</span
      >
      <span *ngIf="type === 'shipping'"
        >{{ isEditMode ? "Edit" : "Add" }} Address</span
      >
    </h1>
    <button
      class="w-6 h-6 flex items-center justify-center cursor-pointer max-md:w-8 max-md:h-8 max-md:text-3xl"
      (click)="onCancel()"
    >
      <span class="text-2xl font-light max-md:text-3xl">×</span>
    </button>
  </header>

  <!-- Content Container -->
  <div
    class="flex flex-col h-full overflow-y-auto pb-[200px] max-md:h-auto max-md:max-h-[calc(90vh-120px)] max-md:pb-20 max-md:px-6 max-md:overflow-y-auto"
  >
    <div class="flex flex-col px-4 mt-6 w-full max-md:px-0 max-md:mt-4">
      <form
        [formGroup]="addressForm"
        (ngSubmit)="onSave()"
        class="max-md:flex-1 max-md:overflow-y-auto"
      >
        <!-- Individual/Company Name Section -->
        <section class="mb-6" *ngIf="type === 'billing'">
          <div class="w-full">
            <label
              class="block text-sm font-bold leading-none text-zinc-700 mb-2"
            >
              Individual/Company Name
            </label>
            <input
              formControlName="companyName"
              type="text"
              placeholder="Registered company name"
              class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
              (input)="onTextInput('companyName', $event)"
              (blur)="onTextBlur('companyName', $event)"
            />
          </div>
        </section>

        <!-- Location Section -->
        <section class="mb-6">
          <!-- Location Header with Current Location -->
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-bold leading-none text-black">Location</h2>
            <button
              type="button"
              class="flex gap-2 items-center text-sm font-medium text-blue-500"
              (click)="useCurrentLocation()"
            >
              <div class="w-5 h-5 flex items-center justify-center">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </div>
              <span>Current Location</span>
            </button>
          </div>

          <!-- Address Fields Container -->
          <div class="w-full space-y-4">
            <!-- Full Address Field (Full Width) -->
            <div class="w-full">
              <div class="flex justify-between items-center mb-2">
                <label
                  class="block text-sm font-bold leading-none text-zinc-700"
                >
                  Flat, House no., Building, Company, Apartment *
                </label>
                <span
                  class="text-xs font-medium transition-colors"
                  [ngClass]="{
                    'text-gray-500':
                      (addressForm.get('flatHouseNo')?.value?.length || 0) <
                      140,
                    'text-orange-500':
                      (addressForm.get('flatHouseNo')?.value?.length || 0) >=
                        140 &&
                      (addressForm.get('flatHouseNo')?.value?.length || 0) <
                        150,
                    'text-red-500 font-bold':
                      (addressForm.get('flatHouseNo')?.value?.length || 0) >=
                      150,
                  }"
                >
                  {{ addressForm.get("flatHouseNo")?.value?.length || 0 }}/150
                </span>
              </div>
              <input
                formControlName="flatHouseNo"
                maxlength="150"
                class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
                placeholder="Enter Flat, House no., Building, Company, Apartment"
                (input)="onTextInput('flatHouseNo', $event)"
                (blur)="onTextBlur('flatHouseNo', $event)"
              />
              <div
                *ngIf="
                  addressForm.get('flatHouseNo')?.invalid &&
                  addressForm.get('flatHouseNo')?.touched
                "
                class="text-red-500 text-xs mt-1"
              >
                <span
                  *ngIf="addressForm.get('flatHouseNo')?.errors?.['required']"
                  >Address is required</span
                >
                <span
                  *ngIf="addressForm.get('flatHouseNo')?.errors?.['minlength']"
                  >Please enter at least 2 characters</span
                >
                <span
                  *ngIf="addressForm.get('flatHouseNo')?.errors?.['maxlength']"
                  >Address cannot exceed 150 characters</span
                >
              </div>
            </div>

            <!-- Two Column Layout for Address Details -->
            <div class="grid grid-cols-2 gap-4 max-md:gap-3">
              <!-- Locality -->
              <div>
                <label
                  class="block text-sm font-bold leading-none text-zinc-700 mb-2"
                >
                  Locality *
                </label>
                <input
                  formControlName="locality"
                  class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
                  placeholder="Nearby place"
                  (input)="onTextInput('locality', $event)"
                  (blur)="onTextBlur('locality', $event)"
                />
                <div
                  *ngIf="
                    addressForm.get('locality')?.invalid &&
                    addressForm.get('locality')?.touched
                  "
                  class="text-red-500 text-xs mt-1"
                >
                  <span
                    *ngIf="addressForm.get('locality')?.errors?.['required']"
                    >Locality is required</span
                  >
                  <span
                    *ngIf="addressForm.get('locality')?.errors?.['minlength']"
                    >Please enter at least 2 characters</span
                  >
                </div>
              </div>

              <!-- PIN -->
              <div>
                <label
                  class="block text-sm font-bold leading-none text-zinc-700 mb-2"
                >
                  PIN *
                </label>
                <div class="relative">
                  <input
                    formControlName="pin"
                    class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
                    placeholder="e.g. 400615"
                    pattern="[0-9]{6}"
                    maxlength="6"
                    (input)="
                      onNumericInput('pin', $event); onPincodeInput($event)
                    "
                  />
                  <!-- Loading spinner -->
                  <div
                    *ngIf="isPincodeLoading"
                    class="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <div
                      class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"
                    ></div>
                  </div>
                </div>
                <!-- Validation errors -->
                <div
                  *ngIf="
                    addressForm.get('pin')?.invalid &&
                    addressForm.get('pin')?.touched
                  "
                  class="text-red-500 text-xs mt-1"
                >
                  <span *ngIf="addressForm.get('pin')?.errors?.['required']"
                    >PIN is required</span
                  >
                  <span *ngIf="addressForm.get('pin')?.errors?.['pattern']"
                    >PIN must be 6 digits</span
                  >
                </div>
                <!-- API error message -->
                <div *ngIf="pincodeError" class="text-red-500 text-xs mt-1">
                  {{ pincodeError }}
                </div>
              </div>
            </div>

            <!-- State and City Row -->
            <div class="grid grid-cols-2 gap-4 max-md:gap-3">
              <!-- State -->
              <div>
                <label
                  class="block text-sm font-bold leading-none text-zinc-700 mb-2"
                >
                  State *
                </label>
                <div class="relative">
                  <input
                    formControlName="state"
                    [readonly]="true"
                    class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 bg-gray-100 cursor-not-allowed"
                    placeholder="Auto-filled from PIN"
                  />
                  <!-- Auto-filled indicator -->
                  <div
                    *ngIf="autoFilledState && !isPincodeLoading"
                    class="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <svg
                      class="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div
                  *ngIf="
                    addressForm.get('state')?.invalid &&
                    addressForm.get('state')?.touched
                  "
                  class="text-red-500 text-xs mt-1"
                >
                  State is required
                </div>
              </div>

              <!-- City -->
              <div>
                <label
                  class="block text-sm font-bold leading-none text-zinc-700 mb-2"
                >
                  City/Town *
                </label>
                <div class="relative">
                  <input
                    formControlName="city"
                    [readonly]="true"
                    class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 bg-gray-100 cursor-not-allowed"
                    placeholder="Auto-filled from PIN"
                  />
                  <!-- Auto-filled indicator -->
                  <div
                    *ngIf="autoFilledCity && !isPincodeLoading"
                    class="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <svg
                      class="w-4 h-4 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div
                  *ngIf="
                    addressForm.get('city')?.invalid &&
                    addressForm.get('city')?.touched
                  "
                  class="text-red-500 text-xs mt-1"
                >
                  City/town is required
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Receiver Details Section - Only for Shipping -->
        <section
          class="mb-6"
          *ngIf="
            (type === 'shipping' || type === 'address') &&
            curentRout !== '/user-profile'
          "
        >
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-bold leading-none text-black">
              Receiver Details
            </h2>
            <span class="text-sm font-medium leading-none text-neutral-500">
              (Optional)
            </span>
          </div>

          <div class="grid grid-cols-2 gap-4 max-md:gap-3">
            <!-- Receiver Name -->
            <div>
              <label
                class="block text-sm font-bold leading-none text-zinc-700 mb-2"
              >
                Receiver Name
              </label>
              <input
                formControlName="receiverName"
                class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
                placeholder="Enter receiver's name"
                (input)="onTextInput('receiverName', $event)"
                (blur)="onTextBlur('receiverName', $event)"
              />
            </div>

            <!-- Receiver Phone -->
            <div>
              <label
                class="block text-sm font-bold leading-none text-zinc-700 mb-2 whitespace-nowrap"
              >
                Receiver Phone Number
              </label>
              <input
                formControlName="receiverPhone"
                class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
                placeholder="Enter receiver's phone no"
                pattern="^[6-9][0-9]{9}$"
                maxlength="10"
                (input)="onNumericInput('receiverPhone', $event)"
              />
              <div
                *ngIf="
                  addressForm.get('receiverPhone')?.invalid &&
                  addressForm.get('receiverPhone')?.touched
                "
                class="text-red-500 text-xs mt-1"
              >
                <span
                  *ngIf="addressForm.get('receiverPhone')?.errors?.['pattern']"
                  >Please enter a valid Indian mobile number (10 digits starting
                  with 6, 7, 8, or 9)</span
                >
              </div>
            </div>
          </div>
        </section>

        <!-- Save As Section (Toggleable) - Only for Shipping -->
        <section class="mb-6" *ngIf="type === 'shipping' || type === 'address'">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold leading-none text-black">Save As</h2>
          </div>

          <div
            class="flex flex-wrap gap-2 items-center text-sm font-medium leading-none"
          >
            <!-- Home Button -->
            <button
              type="button"
              class="flex gap-2 items-center min-h-9"
              (click)="addressForm.get('saveAs')?.setValue('home')"
            >
              <div
                class="flex gap-1 justify-center items-center py-2 pr-3 pl-3 rounded-[39px] border transition-all duration-200"
                [ngClass]="{
                  'bg-white border-gray-200 text-gray-700':
                    addressForm.get('saveAs')?.value !== 'home',
                  'bg-blue-100 border-blue-200 text-blue-500 font-bold':
                    addressForm.get('saveAs')?.value === 'home',
                }"
              >
                <img
                  src="https://cdn.builder.io/api/v1/image/assets/TEMP/480146c4b519ee5f0f669da013fb25e7b18bbfc4?placeholderIfAbsent=true&apiKey=cb544d29e73245039207efcd0a1a6bc7"
                  class="object-contain shrink-0 w-4 h-4 aspect-square"
                  alt="Home icon"
                />
                <span>Home</span>
              </div>
            </button>

            <!-- Work Button -->
            <button
              type="button"
              class="flex gap-2 items-center min-h-9"
              (click)="addressForm.get('saveAs')?.setValue('work')"
            >
              <div
                class="flex gap-1 justify-center items-center py-2 pr-3 pl-3 rounded-[39px] border transition-all duration-200"
                [ngClass]="{
                  'bg-white border-gray-200 text-gray-700':
                    addressForm.get('saveAs')?.value !== 'work',
                  'bg-blue-100 border-blue-200 text-blue-500 font-bold':
                    addressForm.get('saveAs')?.value === 'work',
                }"
              >
                <img
                  src="https://cdn.builder.io/api/v1/image/assets/TEMP/fb892b1fa545b88a6ae4e38c1bf090fd0e13b937?placeholderIfAbsent=true&apiKey=cb544d29e73245039207efcd0a1a6bc7"
                  class="object-contain shrink-0 w-4 h-4 aspect-square"
                  alt="Work icon"
                />
                <span>Work</span>
              </div>
            </button>

            <!-- Others Button -->
            <button
              type="button"
              class="flex gap-2 items-center min-h-9"
              (click)="addressForm.get('saveAs')?.setValue('others')"
            >
              <div
                class="flex gap-1 justify-center items-center py-2 pr-3 pl-3 rounded-[39px] border transition-all duration-200"
                [ngClass]="{
                  'bg-white border-gray-200 text-gray-700':
                    addressForm.get('saveAs')?.value !== 'others',
                  'bg-blue-100 border-blue-200 text-blue-500 font-bold':
                    addressForm.get('saveAs')?.value === 'others',
                }"
              >
                <img
                  src="/assets/location.png"
                  class="object-contain shrink-0 w-4 h-4 aspect-square"
                  alt="Others icon"
                />
                <span>Others</span>
              </div>
            </button>
          </div>
        </section>

        <section
          class="mb-6 w-full"
          *ngIf="addressForm.get('saveAs')?.value === 'others'"
        >
          <div class="w-full">
            <label
              class="block text-sm font-bold leading-none text-zinc-700 mb-2"
            >
              Save address as *
            </label>
            <input
              formControlName="saveAsName"
              class="w-full px-3 py-3 text-base font-medium rounded-lg text-neutral-500 border border-zinc-300 focus:border-blue-500 focus:outline-none bg-white"
              placeholder="Save address as"
              (input)="onTextInput('saveAsName', $event)"
              (blur)="onTextBlur('saveAsName', $event)"
            />
            <div
              *ngIf="
                addressForm.get('saveAsName')?.invalid &&
                addressForm.get('saveAsName')?.touched
              "
              class="text-red-500 text-xs mt-1"
            >
              <span *ngIf="addressForm.get('saveAsName')?.errors?.['required']"
                >This field is required</span
              >
              <span *ngIf="addressForm.get('saveAsName')?.errors?.['minlength']"
                >Please enter at least 2 characters</span
              >
            </div>
          </div>
        </section>
      </form>
    </div>
  </div>

  <!-- Footer -->
  <footer
    class="absolute bottom-0 left-0 right-0 flex gap-3 items-center px-4 py-4 bg-white border-t border-solid border-t-[#C0C5C7] max-md:relative max-md:px-6 max-md:py-6 max-md:gap-4 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:mt-6"
  >
    <button
      type="button"
      (click)="onCancel()"
      class="flex-1 flex justify-center items-center p-3 text-base font-bold text-center text-blue-950 rounded-lg border border-blue-950 hover:bg-blue-50 transition-colors duration-200 max-md:p-4 max-md:text-lg max-md:rounded-xl"
    >
      <span>Cancel</span>
    </button>

    <button
      type="submit"
      [disabled]="!addressForm.valid"
      class="flex-1 flex justify-center items-center p-3 text-base font-bold text-white rounded-lg bg-blue-950 transition-all duration-200 hover:bg-blue-900 disabled:opacity-60 disabled:cursor-not-allowed max-md:p-4 max-md:text-lg max-md:rounded-xl cursor-pointer whitespace-nowrap"
      (click)="onSave()"
    >
      <span *ngIf="type === 'billing'"
        >{{ isEditMode ? "Update" : "Add" }} Billing Address</span
      >
      <span *ngIf="type !== 'billing'">{{
        isEditMode ? "Update" : "Save"
      }}</span>
    </button>
  </footer>
</section>
